import React, { useEffect, useState } from "react";
import { Tabs, Row, Col, Pagination, Input, Spin, Empty, message } from "antd";
import { SearchOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import slugify from "slugify";
import classNames from "classnames";

import CardComponent from "@/components/card";
import Wrapper from "@/layout/Wrapper";
import MainHeading from "@/components/heading/MainHeading";
import ButtonComponent from "@/components/button/Button";

import { getUserBlogs } from "@/services/users/getUserBlogs";

import "@/styles/pages/dashboard/myblogs.scss";
import BlogCard from "@/components/BlogCard";
import { setFormProps } from "@/reducers/commonSlice";
import { useDispatch } from "react-redux";
import SEOHead from "@/components/SEO/SEOHead";

const { TabPane } = Tabs;

const MyBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchBlogs = async (params = {}) => {
    setLoading(true);
    try {
      const response = await getUserBlogs(params);
      const allBlogs = response.rows || [];
      setBlogs(allBlogs);

      // Apply filters on the frontend
      const filtered = filterBlogsByTab(allBlogs, activeTab);
      setFilteredBlogs(filtered);

      // Set total for pagination
      setTotal(response.count || 0);
    } catch (error) {
      console.error("Failed to fetch blogs:", error);
      message.error("Failed to load blogs. Please try again.");
      setBlogs([]);
      setFilteredBlogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Filter blogs based on active tab
  const filterBlogsByTab = (blogsList, tab) => {
    if (tab === "all") return blogsList;
    if (tab === "approved") return blogsList.filter((blog) => blog.isApproved === true);
    if (tab === "pending") return blogsList.filter((blog) => blog.isApproved === false);
    return blogsList;
  };

  useEffect(() => {
    fetchBlogs({ page, search: searchTerm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm]);

  // When tab changes, reapply filter on current blogs
  useEffect(() => {
    const filtered = filterBlogsByTab(blogs, activeTab);
    setFilteredBlogs(filtered);
  }, [activeTab, blogs]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPage(1); // Reset to first page on new search
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    // No need to reload data from API when changing tabs
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    // This will trigger the useEffect to fetch new page data
  };

  const navigateToBlogDetail = (blog) => {
    const authorSlug = slugify(blog.authorName || "author", {
      lower: true,
      strict: true,
      replacement: "-",
      remove: /[^\w\s-]/g,
    });

    navigate(`/blog/${authorSlug}/${blog.slug}/${blog.id}`);
  };

  const navigateToEdit = (blog) => {
    dispatch(setFormProps({ data: blog }));
    navigate("/create-blog");
  };

  // Generate SEO description for My Blogs page
  const generateMyBlogsDescription = () => {
    if (!filteredBlogs || filteredBlogs.length === 0) {
      return "Manage your blog posts in your personal dashboard. Create, edit, and track your published content.";
    }
    
    if (activeTab === "approved") {
      return "View and manage your approved blog posts. Edit or update your published content from your dashboard.";
    } else if (activeTab === "pending") {
      return "Track your pending blog submissions. Monitor the approval status of your recently submitted content.";
    }
    
    return "Your personal blog management dashboard. Create new content, edit existing posts, and track approval status.";
  };
  
  // Extract keywords for SEO
  const extractMyBlogsKeywords = () => {
    if (!filteredBlogs || filteredBlogs.length === 0) {
      return "my blogs, blog dashboard, content management, author dashboard";
    }
    
    // Get keywords from first 3 blogs if available
    const keywordSets = filteredBlogs
      .slice(0, 3)
      .map(blog => blog.keywords || []);
      
    // Flatten and remove duplicates
    const uniqueKeywords = Array.from(new Set(keywordSets.flat()));
    
    // Add default keywords
    const defaultKeywords = ["my blogs", "blog management", "author dashboard"];
    const statusKeywords = activeTab === "approved" ? ["approved blogs"] : 
                          activeTab === "pending" ? ["pending blogs"] : 
                          ["blog posts"];
    
    // Combine all keywords and limit
    return [...uniqueKeywords, ...defaultKeywords, ...statusKeywords].slice(0, 10).join(", ");
  };
  
  // Get the base URL from the environment or use a default
  const baseUrl = import.meta.env.VITE_URL || window.location.origin;
  const myBlogsUrl = `${baseUrl}/dashboard/my-blogs`;
  
  // Get featured image for OG image (first blog's image or a default)
  const featuredImage = 
    filteredBlogs && filteredBlogs.length > 0 && filteredBlogs[0].coverImages && filteredBlogs[0].coverImages.length > 0
      ? filteredBlogs[0].coverImages[0] 
      : `${baseUrl}/assets/images/blogHeaderIcon.svg`;

  const renderBlogCard = (blog) => {
    const coverImage = blog.coverImages?.[0] || "https://placehold.co/300x200?text=No+Image";
    return (
      <Col xs={24} sm={12} lg={8} key={blog.id}>
        <div
          className={classNames("blogCard", blog.isFeatured && "blogCardIsFeatured")}
          style={{
            background: blog.isDeleted
              ? `linear-gradient(#919299d4 100%, rgba(246, 238, 238, 0.65) 0%, url(${coverImage}) center/cover no-repeat)`
              : `linear-gradient(to bottom, rgba(7, 7, 7, 0.4) 100%, rgba(0, 0, 0, 0) 59%, rgba(0, 0, 0, 0.65) 100%), url(${coverImage}) center/cover no-repeat`,
            color: "white",
            border: "1.58828px solid #E9E9E9",
            height: "200px",
            borderRadius: "12.7px",
            position: "relative",
            cursor: "pointer",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: 0,
          }}
          onClick={() => navigateToBlogDetail(blog)}
        >
          {/* Edit pen icon */}
          <EditOutlined
            style={{
              position: "absolute",
              top: 12,
              right: 16,
              fontSize: 20,
              color: "#fff",
              zIndex: 2,
              background: "rgba(0,0,0,0.3)",
              borderRadius: "50%",
              padding: 4,
              cursor: "pointer",
            }}
            title="Edit Blog"
            onClick={(e) => {
              e.stopPropagation();
              navigateToEdit(blog);
            }}
          />
          <div
            style={{
              padding: 16,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span className="blogcarddate">{new Date(blog.updatedAt).toDateString()}</span>
              <span style={{ fontSize: 12 }}>{blog.readingTime || 0} min read</span>
            </div>
            <h4
              className="p-0 m-0 blogcarddatetitle"
              style={{
                color: "#fff",
                fontWeight: 600,
                fontSize: 18,
                margin: "8px 0 4px 0",
                textShadow: "0 1px 4px #000",
              }}
            >
              {blog.title?.length > 80
                ? String(blog.title).slice(0, 80) + "..."
                : String(blog.title)}
            </h4>
            <div
              style={{
                fontSize: 13,
                color: "#f0f0f0",
                textShadow: "0 1px 4px #000",
                marginBottom: 4,
              }}
            >
              {(blog.contents?.[0]?.sectionContent || "").replace(/<[^>]+>/g, "").slice(0, 90)}
              {(blog.contents?.[0]?.sectionContent || "").length > 90 ? "..." : ""}
            </div>
            <span
              style={{
                position: "absolute",
                left: 16,
                top: 12,
                background: blog.isApproved ? "#52c41a" : "#faad14",
                color: "#fff",
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 500,
                padding: "2px 8px",
                zIndex: 2,
              }}
            >
              {blog.isApproved ? "Approved" : "Pending"}
            </span>
          </div>
        </div>
      </Col>
    );
  };

  return (
    <Wrapper>
      <div className="container">
        <SEOHead
          title={`${activeTab === "approved" ? "Approved" : activeTab === "pending" ? "Pending" : "All"} Blogs | Dashboard`}
          description={generateMyBlogsDescription()}
          keywords={extractMyBlogsKeywords()}
          ogTitle={`My Blogs | ${activeTab === "approved" ? "Approved" : activeTab === "pending" ? "Pending" : "All"}`}
          ogDescription={generateMyBlogsDescription()}
          ogImage={featuredImage}
          ogUrl={myBlogsUrl}
          ogType="website"
        />

        <MainHeading title="My Blogs" />

        <div className="my-blogs-header">
          <Input.Search
            placeholder="Search blogs"
            onSearch={handleSearch}
            prefix={<SearchOutlined />}
            className="blog-search-input"
            allowClear
          />

          <div className="" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <ButtonComponent type="primary" onClick={() => navigate("/create-blog")}>
              Create New Blog
            </ButtonComponent>
            <ButtonComponent onClick={() => window.history.back()}>
              {/* back html entity */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-arrow-left"
                viewBox="0 0 16 16"
              >
                <path
                  fillRule="evenodd"
                  d="M11.3 1.3a1 1 0 0 1 0 1.4L5.4 8l5.9 5.3a1 1 0 0 1-1.4 1.4l-6.3-5.7a1 1 0 0 1 0-1.4l6.3-5.7a1 1 0 0 1 1.4 0z"
                />
              </svg>
              Back
            </ButtonComponent>
          </div>
        </div>

        <CardComponent className="my-blogs-container">
          <Tabs activeKey={activeTab} onChange={handleTabChange}>
            <TabPane tab="All Blogs" key="all">
              {renderBlogsContent()}
            </TabPane>
            <TabPane tab="Approved" key="approved">
              {renderBlogsContent()}
            </TabPane>
            <TabPane tab="Pending Approval" key="pending">
              {renderBlogsContent()}
            </TabPane>
          </Tabs>
        </CardComponent>
      </div>
    </Wrapper>
  );

  function renderBlogsContent() {
    if (loading) {
      return (
        <div className="blog-loader">
          <Spin size="large" />
        </div>
      );
    }

    if (!filteredBlogs || filteredBlogs.length === 0) {
      return (
        <Empty
          description={
            <span>
              {searchTerm
                ? `No blogs found for "${searchTerm}"`
                : activeTab === "approved"
                  ? "You don't have any approved blogs yet"
                  : activeTab === "pending"
                    ? "You don't have any pending blogs"
                    : "You haven't created any blogs yet"}
            </span>
          }
        />
      );
    }

    return (
      <>
        <Row gutter={[24, 24]} className="blogs-grid">
          {filteredBlogs.map((blog) => (
            <Col key={blog.id} xs={24} sm={12} md={12} lg={8} xl={8} xxl={6}>
              <BlogCard
                key={blog.id}
                author={blog.authorName}
                date={new Date(blog.updatedAt).toDateString()}
                readTime={blog.readingTime}
                title={blog.title}
                content={stripHtmlTags(blog.contents?.[0]?.sectionContent)}
                coverImage={blog.coverImages?.[0] || "https://placehold.co/300x200?text=No+Image"}
                blogId={blog.id}
                slug={blog.slug}
                newTab
                handleEdit={() => navigateToEdit(blog)}
              />
            </Col>
          ))}
        </Row>

        {activeTab === "all" && total > 10 && (
          <div className="blogs-pagination">
            <Pagination
              current={page}
              total={total}
              onChange={handlePageChange}
              showSizeChanger={false}
            />
          </div>
        )}
      </>
    );
  }
};

export default MyBlogs;

const stripHtmlTags = (htmlContent) => {
  const doc = new DOMParser().parseFromString(htmlContent, "text/html");
  return doc.body.textContent || "";
};
